
import MarketAnalysisService from "@/service/MarketAnalysisService";
import MarketSummaryCard from "@/components/marketAnalysis/MarketSummaryCard";
import SectorWidgetsContainer from "@/components/marketAnalysis/SectorWidgetsContainer";
import { sectorTabConstants, sectorParams } from "@/constants/marketAnalysis";
import { backendExceptionHandler } from "@/mixins";
import Alert from "@/components/Alert";

export default {
    name: "SectorReportsWrapper",

    components: {
        MarketSummaryCard,
        SectorWidgetsContainer,
        Alert,
    },

    mixins: [backendExceptionHandler],

    props: {
        isArchiveSectorPage: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['on-tab-changed'],

    data() {
        return {
            marketSummaryContent: null,
            marketSummaryCreatedAt: '',
            marketSummaryAudioUrl: null,
            activeTab: null,
            isLoading: false,
            widgets: [],
            errorMessage: '',
        }
    },

    async fetch() {
        let locale = this.$getFallbackLocale()
        const routeLang = this.$route.path.split('/')?.[1];
        if (this.$getAvailableLocaleCodes().includes(routeLang)) {
            locale = routeLang
        }

        this.$setLocale(locale)
        this.cancelActiveRequests();
        const axiosSource = this.$axios.CancelToken.source();
        const cancelToken = axiosSource.token;
        this.activeFetchMarketRequest = { cancel: axiosSource.cancel, msg: "Cancel loading data..." };
        this.isLoading = true;
        const sectorFromUrl = this.$getOriginalTabId(this.$route.path.split('/')?.[4]);
        const sector = sectorParams[sectorFromUrl];
        const dateOnUrl = this.$route.params.date;
        const params = {
            lang: locale,
            reportType: 'sector',
            sector: sector,
        }

        if (dateOnUrl) {
            params.date = dateOnUrl;
        }

        const url = dateOnUrl ?
            `/api/market/report/archive` :
            `/api/market/report/last`;            

        try {
            const res = await this.$axios.get(url, { params, cancelToken });
            const data = res.data;

            if (data.body) {
                this.marketSummaryContent = data.body;
                this.marketSummaryCreatedAt = data.created;
                this.widgets = data.widgets ?? [];
                this.marketSummaryAudioUrl = data.audioUrl;
            } else {
                this.marketSummaryContent = null;
                this.marketSummaryCreatedAt = '';
                this.marketSummaryAudioUrl = null;
                this.widgets = [];
            }

            this.activeFetchMarketRequest = null;
        } catch (error) {
            this.marketSummaryContent = null
            this.marketSummaryCreatedAt = ''
            this.marketSummaryAudioUrl = null
            this.widgets = []
            this.handleBackendException(error, this.$t('common.errors.generalError'))
        } finally {
            this.isLoading = false;
        }
    },

    computed: {
        showWidgetsArea() {
            return !this.isArchiveSectorPage && (this.isLoading || this.widgets.length > 0);
        },
        sectorTabs() {
            return [
                {
                    displayedName: this.$t('component.sectorReportsWrapper.basicMaterials'),
                    key: sectorTabConstants.basicMaterials,
                    path: this.$getTranslatedRoutePath(`/market-analysis/sector-reports/${sectorTabConstants.basicMaterials}/`)
                },
                {
                    displayedName: this.$t('component.sectorReportsWrapper.communicationServices'),
                    key: sectorTabConstants.communicationServices,
                    path: this.$getTranslatedRoutePath(`/market-analysis/sector-reports/${sectorTabConstants.communicationServices}/`)
                },
                {
                    displayedName: this.$t('component.sectorReportsWrapper.consumerCyclical'),
                    key: sectorTabConstants.consumerCyclical,
                    path: this.$getTranslatedRoutePath(`/market-analysis/sector-reports/${sectorTabConstants.consumerCyclical}/`)
                },
                {
                    displayedName: this.$t('component.sectorReportsWrapper.consumerDefensive'),
                    key: sectorTabConstants.consumerDefensive,
                    path: this.$getTranslatedRoutePath(`/market-analysis/sector-reports/${sectorTabConstants.consumerDefensive}/`)
                },
                {
                    displayedName: this.$t('component.sectorReportsWrapper.energy'),
                    key: sectorTabConstants.energy,
                    path: this.$getTranslatedRoutePath(`/market-analysis/sector-reports/${sectorTabConstants.energy}/`)
                },
                {
                    displayedName: this.$t('component.sectorReportsWrapper.financialServices'),
                    key: sectorTabConstants.financialServices,
                    path: this.$getTranslatedRoutePath(`/market-analysis/sector-reports/${sectorTabConstants.financialServices}/`)
                },
                {
                    displayedName: this.$t('component.sectorReportsWrapper.healthcares'),
                    key: sectorTabConstants.healthcare,
                    path: this.$getTranslatedRoutePath(`/market-analysis/sector-reports/${sectorTabConstants.healthcare}/`)
                },
                {
                    displayedName: this.$t('component.sectorReportsWrapper.industrials'),
                    key: sectorTabConstants.industrials,
                    path: this.$getTranslatedRoutePath(`/market-analysis/sector-reports/${sectorTabConstants.industrials}/`)
                },
                {
                    displayedName: this.$t('component.sectorReportsWrapper.realEstate'),
                    key: sectorTabConstants.realEstate,
                    path: this.$getTranslatedRoutePath(`/market-analysis/sector-reports/${sectorTabConstants.realEstate}/`)
                },
                {
                    displayedName: this.$t('component.sectorReportsWrapper.technology'),
                    key: sectorTabConstants.technology,
                    path: this.$getTranslatedRoutePath(`/market-analysis/sector-reports/${sectorTabConstants.technology}/`)
                },
                {
                    displayedName: this.$t('component.sectorReportsWrapper.utilities'),
                    key: sectorTabConstants.utilities,
                    path: this.$getTranslatedRoutePath(`/market-analysis/sector-reports/${sectorTabConstants.utilities}/`)
                },
            ];
        }
    },

    watch: {
        '$route.params.sector_id': {
            immediate: true,
            handler(sectorId) {
                this.activeTab = this.$getOriginalTabId(sectorId);
            }
        },
        activeTab: {
            immediate: true,
            handler(newTab) {                
                this.$emit('on-tab-changed', newTab)
            }
        }
    },

    created() {
        this.marketAnalysisService = new MarketAnalysisService(this.$axios);
    },

    beforeDestroy() {
        this.cancelActiveRequests();
    },

    methods: {
        cancelActiveRequests() {
            if (this.activeFetchMarketRequest) {
                this.activeFetchMarketRequest.cancel()
            }
        },
    }
};
